import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiService from "../services/ApiService";
import AuthService from "../services/AuthService";
import Loader from "../components/Loader"; 
import ImageUpload from "../components/ImageUpload";

const Account = () => {
  const navigate = useNavigate();
  const user = AuthService.getCurrentUser();
  const isCustomer = user && user.role === 'customer';

  const [tab, setTab] = useState("email");
  const [email, setEmail] = useState(user?.email || '');
  const [username, setUsername] = useState(user?.name || '');
  const [phone, setPhone] = useState(() => {
    if (user?.phone && user.phone.startsWith('+353')) {
        return user.phone.substring(4);
    }
    return user?.phone || '';
  });
  const [phoneCode, setPhoneCode] = useState('+353');
  const [picture, setPicture] = useState(user?.picture || null);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoader, setIsLoader] = useState(false);

  useEffect(() => {
    if(!user) navigate('/login');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoader(true);
    try {
        const formData = {
            "email": email,
            "username": username,
            "old_password": oldPassword,
            "new_password": newPassword,
            "new_password_confirmation": confirmPassword,
          };

        if(!newPassword) {
            delete formData.old_password;
            delete formData.new_password;
            delete formData.new_password_confirmation;
        } else {
             if (newPassword !== confirmPassword) {
                toast.error("Passwords do not match");
                setIsLoader(false);
                return;
            }
        }

        const url = isCustomer ? '/customer/update-profile' : '/update-profile';

        const response = await ApiService.request({
            method: "POST",
            url: url, 
            data: formData
        });
        const data = response.data;
        if (data.status) {
            const updatedUser = { ...user, email: email, name: username };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            
            toast.success(data.message);
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } else {
            toast.error(data.message);
        }
    } catch (error) {
        console.error(error);
        toast.error(error.response?.data?.message || "Failed to update profile");
    } finally {
        setIsLoader(false);
    }
  };

  const handleImageChange = (name, file) => {
    if (name === 'picture') {
      setPicture(file);
      if (file) {
        setPicture(URL.createObjectURL(file));
      } else {
        setPicture(null);
      }
    }
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
            <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                    <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                        Account
                    </h1>
                    <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                        <li className="breadcrumb-item text-muted">
                            <Link to="/dashboard" className="text-muted text-hover-primary">Home</Link>
                        </li>
                        <li className="breadcrumb-item">
                            <span className="bullet bg-gray-400 w-5px h-2px"></span>
                        </li>
                        <li className="breadcrumb-item text-muted">Account</li>
                    </ul>
                </div>
                <div className="d-flex align-items-center gap-2 gap-lg-3"></div>
            </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
            <div id="kt_app_content_container" className="app-container container-fluid">
                <div className="card">
                    <nav>
                        <div className="nav nav-tabs" id="nav-tab" role="tablist">
                        <button
                            className={`nav-link fs-lg-5 ${tab === "email" ? "active" : ""}`}
                            onClick={() => setTab("email")}
                            id="nav-profile-tab"
                            type="button"
                        >
                            <i className="fas fa-envelope me-2"></i> Account Info
                        </button>
                        <button
                            className={`nav-link fs-lg-5 ${tab === "password" ? "active" : ""}`}
                            onClick={() => setTab("password")}
                            id="nav-contact-tab"
                            type="button"
                        >
                            <i className="fas fa-key me-2"></i> Change Password
                        </button>
                        </div>
                    </nav>
                    <div className="card-body pt-0">
                        <div className="mb-8 text-center"></div>
                        <div className="row g-9 mb-8">
                        <div className="col-md-12">
                            <div className="tab-content mt-5" id="nav-tabContent">
                            {tab === "email" && (
                                <div className="tab-pane fade show active">
                                <form className="form" onSubmit={handleSubmit}>
                                    <div className="d-flex my-3 flex-column">
                                        <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                            <span className="required">Name</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            className="form-control form-control-solid"
                                            placeholder="Enter Name"
                                        />
                                    </div>
                                    <div className="d-flex my-3 flex-column">
                                        <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                            <span className="required">Phone</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">{phoneCode}</span>
                                            <input
                                                type="text"
                                                className="form-control form-control-solid"
                                                value={phone}
                                                onChange={(e) => {
                                                    const val = e.target.value.replace(/\D/g, '');
                                                    setPhone(val);
                                                }}
                                                placeholder="Phone Number"
                                            />
                                        </div>
                                    </div>
                                    <div className="d-flex my-3 flex-column">
                                        <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                            <span className="required">Email</span>
                                        </label>
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="form-control form-control-solid"
                                            placeholder="Enter Email"
                                        />
                                    </div>
                                    {/* Image upload removed as it requires more complex backend handling not currently in scope */}
                                    
                                    <button
                                    type="submit"
                                    className="btn btn-base my-5"
                                    disabled={isLoader}
                                    >
                                    <span className="indicator-label">
                                        {!isLoader ? "Update Info" : <Loader size="20" text="Updating" />}
                                    </span>
                                    </button>
                                </form>
                                </div>
                            )}
                            {tab === "password" && (
                                <div className="tab-pane fade show active">
                                <form className="form" onSubmit={handleSubmit}>
                                    <div className="row">
                                    <div className="col-md-12">
                                        <label className="required fs-6 fw-semibold mb-2">
                                        Current Password
                                        </label>
                                        <input
                                        type="password" required
                                        className="form-control form-control-solid"
                                        placeholder="Enter Your Old Password"
                                        value={oldPassword}
                                        onChange={(e) => setOldPassword(e.target.value)}
                                        />
                                        <br />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="required fs-6 fw-semibold mb-2">
                                        New Password
                                        </label>
                                        <input required
                                        type="password"
                                        className="form-control form-control-solid"
                                        placeholder="Enter New Password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="required fs-6 fw-semibold mb-2">
                                        Confirm Password
                                        </label>
                                        <input
                                        type="password" required
                                        className="form-control form-control-solid"
                                        placeholder="Enter Confirm Password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        />
                                    </div>
                                    </div>
                                    <button
                                    type="submit"
                                    className="btn btn-base my-5"
                                    disabled={isLoader}
                                    >
                                    <span className="indicator-label">
                                        {!isLoader ? "Update Password" : <Loader size="20" text="Updating" />}
                                    </span>
                                    </button>
                                </form>
                                </div>
                            )}
                            </div>
                        </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </div>
  );
};

export default Account;